/* eslint-disable no-unused-vars */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import safelyHoistStatics from '../../helpers/safelyHoistStatics';
import Analytics from '../../helpers/Tracking/Analytics';

// Analytics singleton is bound to wrapped component as a prop,
// so that methods from it may be used directly
const analytics = new Analytics();

const getValuesAndCallback = (state, observers, previousValues) =>
  Object.keys(observers).reduce((obj, key) => {
    const [selector, callback] = observers[key];

    const previousValue = previousValues[key];
    const value = selector(state);

    if (previousValue !== value) {
      callback({ value, previousValue, state, analytics });
      // eslint-disable-next-line no-param-reassign
      obj[key] = value;
    }
    return obj;
  }, previousValues);

const subscribe = (observers, invokeImmediately = false) => store => {
  let values = {};

  if (invokeImmediately) {
    values = getValuesAndCallback(store.getState(), observers, values);
  }

  return store.subscribe(state => {
    getValuesAndCallback(state, observers, values);
  });
};

/**
 * A store enhancer to tap into dispatch to see if we need to fire any analytics events
 * Works similar to a middleware, except it is composable,
 * and is not static like middleware are.
 * @param createStore
 * @returns {function(*=, *=, *=)}
 */
export function actionListenersStoreEnhancer(createStore) {
  return (reducer, initialState, enhancer) => {
    const actionListeners = {};
    const store = createStore(reducer, initialState, enhancer);
    const { dispatch } = store;

    store.dispatch = action => {
      const result = dispatch(action);
      if (
        typeof action === 'object' &&
        action.type &&
        actionListeners[action.type]
      ) {
        actionListeners[action.type].forEach(listener =>
          listener(action, analytics, store.getState)
        );
      }

      return result;
    };

    store.addActionListener = (actionType, listener) => {
      actionListeners[actionType] = (actionListeners[actionType] || []).concat(
        listener
      );

      return () => {
        actionListeners[actionType] = actionListeners[actionType].filter(
          l => l !== listener
        );
      };
    };

    return store;
  };
}

/**
 * Decorator component to wrap components with Analytics listening capabilities in a reactive way
 *
 * @param valueObservers - Are subscribers fired after state is updated,
 * looking specifically at value
 * @param actionListeners - Act as middleware bound to specific actions keys
 * @param invokeImmediately - Should we process immediately or just onChange
 */
const withAnalytics = (
  valueObservers,
  actionListeners,
  invokeImmediately = true
) => BaseComponent => {
  class WithAnalytics extends Component {
    constructor(props, context) {
      super(props);

      if (!context.store) {
        // Throw an error if the store is not available to us
        throw new Error(
          'This component can only be used on a Container component!'
        );
      }

      this.store = context.store;
    }

    componentWillMount() {
      this.unsubscribe = subscribe(valueObservers, invokeImmediately)(
        this.store
      );
      this.removeActionListenerList = Object.keys(actionListeners).map(
        actionType =>
          this.store.addActionListener(actionType, actionListeners[actionType])
      );
    }

    componentWillUnmount() {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.removeActionListenerList.forEach(removeListener =>
          removeListener()
        );
      }
    }

    render() {
      return React.createElement(BaseComponent, { ...this.props, analytics });
    }
  }

  const displayName =
    BaseComponent.displayName || BaseComponent.name || 'Component';

  WithAnalytics.displayName = `WithAnalytics[${displayName}]`;

  safelyHoistStatics(WithAnalytics, BaseComponent);

  WithAnalytics.contextTypes = {
    store: PropTypes.object.isRequired
  };

  return WithAnalytics;
};

export default withAnalytics;
